import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useUser } from '../../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Mini-modal that creates an Activity (call or note) linked to the plan item's
 * company/contact/opportunity and marks the plan item as done.
 */
const QuickLogDialog = ({ open, onOpenChange, kind, planItem, onSuccess }) => {
  const { currentUser } = useUser();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [saving, setSaving] = useState(false);

  const isCall = kind === 'call';
  const label = isCall ? 'Registrar llamada' : 'Añadir nota';

  const reset = () => {
    setTitle('');
    setSummary('');
  };

  const handleSave = async () => {
    if (!summary.trim() && !title.trim()) {
      toast.error('Añade al menos un título o descripción');
      return;
    }
    setSaving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        title: title || (isCall ? 'Llamada registrada' : 'Nota rápida'),
        type: isCall ? 'call' : 'reflexion',
        date: today,
        owner: currentUser?.name,
        summary: summary || null,
        company_id: planItem?.company_id || null,
        contact_id: planItem?.contact_id || null,
        opportunity_id: planItem?.opportunity_id || null,
      };
      await axios.post(`${API}/activities`, payload, {
        headers: { 'X-User-Email': currentUser?.email },
      });

      // Mark plan item as done
      const token = localStorage.getItem('crm_token');
      if (planItem?.item_type && planItem?.item_id) {
        await axios.post(
          `${API}/dashboard/weekly-plan/item/toggle-done`,
          { item_type: planItem.item_type, item_id: planItem.item_id, done: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success(isCall ? 'Llamada registrada' : 'Nota añadida');
      reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      console.error(err);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md" data-testid={`quick-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>
            {planItem?.title && <>Relacionado con: <span className="font-medium">{planItem.title}</span></>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Título (opcional)</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={isCall ? 'Llamada con...' : 'Nota: ...'}
              data-testid="quick-log-title"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              {isCall ? 'Resumen de la llamada' : 'Contenido de la nota'}
            </label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={isCall ? 'De qué habéis hablado, próximos pasos...' : 'Qué has observado...'}
              className="min-h-[100px]"
              data-testid="quick-log-summary"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="quick-log-save">
            {saving ? 'Guardando...' : 'Guardar y marcar hecho'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickLogDialog;
